// Background starfield + constellations.
//
// Stars and constellation lines are positioned in ABSOLUTE pixel coordinates
// inside an SVG that's sized to (viewportWidth × computedFieldHeight). The
// field height covers the viewport plus enough vertical room for the parallax
// travel across the full document scroll, so stars never run out as the
// translateY transform slides the field upward.
//
// Stars do NOT stretch with viewport — circle radii are in pixels and don't
// scale. On window resize the field is rebuilt (debounced from moon.js) so
// dimensions stay consistent.

const SVG_NS = "http://www.w3.org/2000/svg";

// ── Tuning ─────────────────────────────────────────────────────────────────

export const starsConfig = {
  bgStarCount: 240,
  bgStarMinRadius: 0.4,
  bgStarMaxRadius: 1.4,
  bgStarMinDuration: 3.0,    // seconds
  bgStarMaxDuration: 8.0,
  bgStarMinOpacity: 0.15,    // dim peak
  bgStarMaxOpacity: 0.85,    // bright peak
  parallaxFactor: 0.18,      // 0 = no parallax; 1 = scrolls with content
  fieldVerticalBuffer: 120,  // extra px below the parallax travel range
  moonAvoidanceRadiusPx: 100,
};

// Constellations: anchored at a percentage of the INITIAL viewport (so they
// land where you can see them when the page first loads). Each star's
// (dx, dy) is pixel offset from the anchor; lines connect star indices.
const CONSTELLATIONS = [
  {
    name: "crux",
    anchorPct: { x: 12, y: 70 },
    stars: [
      { dx:   0, dy:  30, r: 1.7 },
      { dx:  18, dy:  -8, r: 1.5 },
      { dx:   0, dy: -40, r: 1.6 },
      { dx: -22, dy:  -8, r: 1.3 },
      { dx:  25, dy:  18, r: 1.0 },
    ],
    lines: [[0, 2], [1, 3]],
  },
  {
    name: "orions-belt",
    anchorPct: { x: 86, y: 22 },
    stars: [
      { dx: -25, dy: -8, r: 1.6 },
      { dx:   0, dy:  0, r: 1.5 },
      { dx:  25, dy:  8, r: 1.6 },
    ],
    lines: [[0, 1], [1, 2]],
  },
  {
    name: "w",
    anchorPct: { x: 35, y: 16 },
    stars: [
      { dx: -50, dy:  5, r: 1.4 },
      { dx: -25, dy: -8, r: 1.3 },
      { dx:   0, dy:  5, r: 1.5 },
      { dx:  25, dy: -8, r: 1.3 },
      { dx:  50, dy:  5, r: 1.4 },
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
  {
    // Hidden heart — small, slightly subdued so it reads as a constellation
    // rather than a sticker. Discoverable on close inspection.
    name: "heart",
    anchorPct: { x: 65, y: 72 },
    stars: [
      { dx:   0, dy:  34, r: 1.6 }, // 0: bottom apex
      { dx: -22, dy:  10, r: 1.3 }, // 1: lower-left curve
      { dx: -28, dy:  -8, r: 1.3 }, // 2: mid-left
      { dx: -22, dy: -22, r: 1.4 }, // 3: top of left lobe
      { dx:  -8, dy: -24, r: 1.2 }, // 4: between lobes (left side)
      { dx:   0, dy: -14, r: 1.3 }, // 5: center dip
      { dx:   8, dy: -24, r: 1.2 }, // 6: between lobes (right side)
      { dx:  22, dy: -22, r: 1.4 }, // 7: top of right lobe
      { dx:  28, dy:  -8, r: 1.3 }, // 8: mid-right
      { dx:  22, dy:  10, r: 1.3 }, // 9: lower-right curve
    ],
    lines: [
      [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
      [5, 6], [6, 7], [7, 8], [8, 9], [9, 0],
    ],
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function fieldDimensions() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const docHeight = document.documentElement.scrollHeight;
  const maxScroll = Math.max(0, docHeight - vh);
  const height = vh + maxScroll * starsConfig.parallaxFactor + starsConfig.fieldVerticalBuffer;
  return { width: vw, height };
}

function moonCenterPx() {
  return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

function generateBgStars(width, height) {
  const stars = [];
  const target = starsConfig.bgStarCount;
  const moon = moonCenterPx();
  const avoid = starsConfig.moonAvoidanceRadiusPx;
  let attempts = 0;
  while (stars.length < target && attempts < target * 6) {
    attempts++;
    const x = Math.random() * width;
    const y = Math.random() * height;
    if (avoid > 0 && Math.hypot(x - moon.x, y - moon.y) < avoid) continue;
    stars.push({
      x, y,
      r: rand(starsConfig.bgStarMinRadius, starsConfig.bgStarMaxRadius),
      duration: rand(starsConfig.bgStarMinDuration, starsConfig.bgStarMaxDuration),
      delay: rand(0, starsConfig.bgStarMaxDuration),
      min: rand(starsConfig.bgStarMinOpacity, starsConfig.bgStarMinOpacity + 0.15),
      max: rand(starsConfig.bgStarMaxOpacity - 0.2, starsConfig.bgStarMaxOpacity),
      glowPx: rand(0.5, 1.6),
    });
  }
  return stars;
}

function makeCircle(cx, cy, r, className, vars) {
  const c = document.createElementNS(SVG_NS, "circle");
  c.setAttribute("cx", cx);
  c.setAttribute("cy", cy);
  c.setAttribute("r", r);
  if (className) c.setAttribute("class", className);
  if (vars) {
    let style = "";
    for (const [k, v] of Object.entries(vars)) style += `--${k}:${v};`;
    c.setAttribute("style", style);
  }
  return c;
}

function makeLine(x1, y1, x2, y2) {
  const l = document.createElementNS(SVG_NS, "line");
  l.setAttribute("x1", x1);
  l.setAttribute("y1", y1);
  l.setAttribute("x2", x2);
  l.setAttribute("y2", y2);
  return l;
}

function constellationToPixels(con, vw, vh) {
  const ax = (con.anchorPct.x / 100) * vw;
  const ay = (con.anchorPct.y / 100) * vh;
  return {
    stars: con.stars.map((s) => ({ x: ax + s.dx, y: ay + s.dy, r: s.r })),
    lines: con.lines,
  };
}

// ── Public ─────────────────────────────────────────────────────────────────

export function renderStarfield() {
  const svg = document.querySelector(".stars");
  if (!svg) return;

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const { width, height } = fieldDimensions();
  svg.setAttribute("width", width);
  svg.setAttribute("height", height);
  svg.style.width = `${width}px`;
  svg.style.height = `${height}px`;

  const vh = window.innerHeight;
  const consAbs = CONSTELLATIONS.map((c) => constellationToPixels(c, width, vh));

  // Constellation lines first (under the stars).
  for (const con of consAbs) {
    for (const [a, b] of con.lines) {
      svg.appendChild(makeLine(con.stars[a].x, con.stars[a].y, con.stars[b].x, con.stars[b].y));
    }
  }

  // Constellation stars — brighter, slower pulse.
  for (const con of consAbs) {
    for (const s of con.stars) {
      svg.appendChild(
        makeCircle(s.x, s.y, s.r, "bright", {
          "twinkle-dur": `${rand(5, 10)}s`,
          "twinkle-delay": `${rand(0, 5)}s`,
          "star-min": "0.55",
          "star-max": "1.0",
          "star-glow": "3px",
        }),
      );
    }
  }

  // Background stars across the full field area.
  for (const s of generateBgStars(width, height)) {
    svg.appendChild(
      makeCircle(s.x, s.y, s.r, null, {
        "twinkle-dur": `${s.duration}s`,
        "twinkle-delay": `${s.delay}s`,
        "star-min": s.min.toFixed(2),
        "star-max": s.max.toFixed(2),
        "star-glow": `${s.glowPx.toFixed(1)}px`,
      }),
    );
  }
}

// Slide the entire starfield upward as the user scrolls. Lower factor = more
// distant feel (less motion). Called from the render loop in moon.js.
export function applyStarsParallax(scrollY) {
  const svg = document.querySelector(".stars");
  if (!svg) return;
  const offset = -scrollY * starsConfig.parallaxFactor;
  svg.style.transform = `translateY(${offset}px)`;
}
